import React, { useState, useRef, useEffect } from 'react'
import classNames from 'classnames'
import styles from './ListDropDown.module.css'

const EMPTY_IDS = []

export const ListDropdown = ({
  data = EMPTY_IDS,
  checkedItems,
  checkedIds = EMPTY_IDS,
  allItemsName,
  lockedIds = EMPTY_IDS,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [listData, setListData] = useState([])
  const [nameList, setNameList] = useState('')
  const [idList, setIdList] = useState([])
  const buttonRef = useRef(null)
  const dropdownRef = useRef(null)

  const lockedIdSet = new Set(
    (lockedIds || EMPTY_IDS)
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id))
  )
  const isLocked = (id) => lockedIdSet.has(Number(id))
  const lockedKey = (lockedIds || EMPTY_IDS).join(',')
  const checkedKey = (checkedIds || EMPTY_IDS).join(',')

  useEffect(() => {
    const carList = convertArrayForChecking(data, checkedIds, lockedIds)
    setListData(carList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, checkedKey, lockedKey])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const fetchNameList = () => {
      if (listData.length === 0) {
        setNameList('')
        setIdList([])
        return
      }
      const checked = listData.filter((item) => item.isChecked)
      const checkedWithoutFirst = checked.filter((item) => item.id !== -2)
      let buttonSign
      let ids
      if (checkedWithoutFirst.length === 0) {
        const allItems = listData.find((item) => item.id === -2)
        buttonSign = allItems ? allItems.name : ''
        ids = allItems ? [allItems.id] : []
      } else {
        buttonSign = checkedWithoutFirst.map((item) => item.name).join(', ')
        ids = checkedWithoutFirst.map((item) => item.id)
      }
      setNameList(buttonSign)
      setIdList(ids)
    }
    fetchNameList()
  }, [listData])

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev)
  }

  const toggleCheckbox = (item) => {
    if (isLocked(item.id)) return

    let updatedList
    if (item.id === -1) {
      updatedList = listData.map((i) => ({
        ...i,
        isChecked: i.id === -1 || isLocked(i.id),
      }))
    } else {
      updatedList = listData.map((i) => {
        if (i.id === item.id) {
          return { ...i, isChecked: !i.isChecked }
        }
        return i
      })

      updatedList = updatedList.map((i) =>
        isLocked(i.id) ? { ...i, isChecked: true } : i
      )

      const hasCheckedItems = updatedList.some(
        (i) => i.isChecked && i.id !== -1
      )

      updatedList = updatedList.map((i) => {
        if (i.id === -1) {
          return { ...i, isChecked: !hasCheckedItems }
        }
        return i
      })
    }
    setListData(updatedList)
  }

  const handleClickDoneButton = () => {
    setIsDropdownOpen(false)
    const selected = listData
      .filter((item) => item.isChecked)
      .map((item) => item.id)

    const hasAll = selected.includes(-1)
    const lockedArr = Array.from(lockedIdSet)
    const ensured = hasAll
      ? selected
      : Array.from(
          new Set([
            ...selected.filter((id) => id !== -1).map(Number),
            ...lockedArr,
          ])
        )

    checkedItems(ensured)
  }

  function convertArrayForChecking(data, checkedIds, lockedIdsProp = []) {
    const locked = new Set(
      (lockedIdsProp || [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id))
    )
    const checked = (checkedIds || []).map((id) => Number(id))
    const isAllChecked = checked[0] === -1

    if (checked.length > 0) {
      return [
        {
          id: -1,
          name: allItemsName,
          isChecked: isAllChecked,
          active: false,
          number: '',
          vin: '',
          mileage: 0,
          insurance_end_rb: '',
          insurance_end_rf: '',
          state_inspection_end: '',
          cargo_volume: 0,
          cargo_weight: 0,
          image: [],
        },
        ...data.map((item) => ({
          ...item,
          isChecked:
            locked.has(Number(item.id)) ||
            isAllChecked ||
            checked.includes(Number(item.id)),
        })),
      ]
    }

    return [
      {
        id: -1,
        name: allItemsName,
        isChecked: locked.size === 0,
        active: false,
        number: '',
        vin: '',
        mileage: 0,
        insurance_end_rb: '',
        insurance_end_rf: '',
        state_inspection_end: '',
        cargo_volume: 0,
        cargo_weight: 0,
        image: [],
      },
      ...data.map((item) => ({
        ...item,
        isChecked: locked.has(Number(item.id)),
      })),
    ]
  }

  return (
    <div>
      <div className={styles.dropdown_container} ref={dropdownRef}>
        <button
          type="button"
          ref={buttonRef}
          onClick={toggleDropdown}
          className={classNames(styles.dropdown_button, styles.btn_input, {
            [styles.active]: isDropdownOpen,
          })}
        >
          {' '}
          {nameList}
        </button>
        {isDropdownOpen && (
          <div className={styles.dropdown_menu}>
            <div className={styles.dropdown_content}>
              {listData.map((item) => {
                const locked = isLocked(item.id)
                return (
                  <div
                    className={classNames(styles.dropdown_item, {
                      [styles.dropdownItemLocked]: locked,
                    })}
                    key={item.id}
                  >
                    <input
                      type="checkbox"
                      onChange={(e) => {
                        if (locked) {
                          e.preventDefault()
                          return
                        }
                        toggleCheckbox(item)
                      }}
                      onClick={(e) => {
                        if (locked) e.preventDefault()
                      }}
                      checked={!!item.isChecked}
                      disabled={locked}
                      readOnly={locked}
                    />
                    <label
                      className={classNames('hidden peer', styles.body, {
                        [styles.labelLocked]: locked,
                      })}
                      onClick={(e) => {
                        if (locked) {
                          e.preventDefault()
                          e.stopPropagation()
                          return
                        }
                        toggleCheckbox(item)
                      }}
                    >
                      {item.name}
                    </label>
                  </div>
                )
              })}
            </div>
            <button
              className={styles.btnBlueOutline}
              onClick={handleClickDoneButton}
            >
              Применить
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
